import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import optionTypes from '../constants/option-types';
import ElementSettingsContext from '../helpers/element-settings-context';
import getOptionBatchChanges from '../helpers/get-option-batch-changes';
import ToggleOption from './toggle-option';
import ToggleableSectionOption from './toggleble-section-option';
import ColorSchemeOption from './color-scheme-option';
import ActionRadioGroupOption from './action-radio-group-option';
import SliderOption from './slider-option';
import SelectOption from './select-option';
import SectionOption from './section-option';

const { Divider } = designSystem;

function renderSubOptions(options, settingId) {
  return options?.map((subOption) => (
    <ElementSettingsOption key={subOption.id} option={subOption} settingId={settingId} />
  ));
}

function ElementSettingsOption({ option, settingId }) {
  const { updateState } = useContext(ElementSettingsContext);

  const handleUpdateState = useCallback(
    (batchChanges) => {
      updateState(option, batchChanges);
    },
    [option, updateState]
  );

  const handleUpdateValue = useCallback(
    (value, attributes = option.attributes) => {
      const batchChanges = getOptionBatchChanges({
        elementIds: option.elementIds,
        attributes,
        value,
      });

      handleUpdateState(batchChanges);
    },
    [option, handleUpdateState]
  );

  const handleUpdateWithAdditionalBatch = useCallback(
    (value, batch, attributes = option.attributes) => {
      const batchChanges = getOptionBatchChanges({
        elementIds: option.elementIds,
        attributes,
        value,
      });

      if (batch) {
        Object.keys(batch).forEach((elementId) => {
          batchChanges[elementId] = {
            ...batchChanges[elementId],
            ...batch[elementId],
          };
        });
      }

      handleUpdateState(batchChanges);
    },
    [option, handleUpdateState]
  );

  switch (option.type) {
    case optionTypes.DIVIDER:
      return <Divider appearance="light-grey" />;
    case optionTypes.CUSTOM:
      return <div>{option.component}</div>;
    case optionTypes.TOGGLE: {
      return (
        <ToggleOption option={option} settingId={settingId} onUpdateValue={handleUpdateValue} />
      );
    }
    case optionTypes.SECTION: {
      return (
        <SectionOption option={option}>{renderSubOptions(option.options, settingId)}</SectionOption>
      );
    }
    case optionTypes.TOGGLEABLE_SECTION: {
      return (
        <ToggleableSectionOption
          option={option}
          settingId={settingId}
          onUpdateState={handleUpdateState}
        >
          {renderSubOptions(option.options, settingId)}
        </ToggleableSectionOption>
      );
    }
    case optionTypes.COLOR_SCHEME: {
      return (
        <ColorSchemeOption
          option={option}
          settingId={settingId}
          onUpdateValue={handleUpdateValue}
        />
      );
    }
    case optionTypes.ACTION_RADIO_GROUP: {
      return (
        <ActionRadioGroupOption
          option={option}
          settingId={settingId}
          onUpdateValue={handleUpdateValue}
        />
      );
    }

    case optionTypes.SLIDER: {
      return (
        <SliderOption option={option} settingId={settingId} onUpdateValue={handleUpdateValue} />
      );
    }

    case optionTypes.SELECT: {
      return (
        <SelectOption
          option={option}
          settingId={settingId}
          onUpdateValue={handleUpdateWithAdditionalBatch}
        />
      );
    }

    default:
      return null;
  }
}

ElementSettingsOption.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(Object.values(optionTypes)).isRequired,
    elementIds: PropTypes.arrayOf(PropTypes.string),
    attributes: PropTypes.arrayOf(PropTypes.string),
    options: PropTypes.array,
    title: PropTypes.string,
    component: PropTypes.element,
    popover: PropTypes.shape({
      title: PropTypes.string,
      imgSrc: PropTypes.string,
    }),
    computeFallbackValue: PropTypes.func,
  }).isRequired,
  settingId: PropTypes.string.isRequired,
};

export default ElementSettingsOption;
